/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
	ClipboardCheckIcon,
	ChartBarIcon,
	CloudIcon,
	MenuIcon,
	KeyIcon,
	XIcon,
	CollectionIcon,
	CreditCardIcon,
	GlobeIcon,
	ArrowSmRightIcon,
	ExternalLinkIcon,
	BookOpenIcon,
} from '@heroicons/react/outline'
import { useCarbonStatus, useUserData } from '../lib/hooks'
import { NavLink } from 'react-router-dom'
import NeutralGreenBadge from './badges/NeutralGreenBadge'
import NegativeGreenBadge from './NegativeGreenBadge'
import { CarbonStatus } from '../lib/models'
import { getMonthRef } from '../lib/util'
import moment from 'moment'

const navigation = [
	{ name: 'Carbon Tracker', href: '/', icon: CloudIcon },
	{ name: 'Emission History', href: '/history', icon: ChartBarIcon },
	{ name: 'Projects', href: '/projects', icon: CollectionIcon },
	{ name: 'Orders', href: '/orders', icon: ClipboardCheckIcon },
	{ name: 'Billing', href: '/billing', icon: CreditCardIcon },
	{ name: 'API Key', href: '/api-key', icon: KeyIcon },
]

function classNames(...classes: any) {
	return classes.filter(Boolean).join(' ')
}

export default function DashboardLayout({ children }: { children?: any }) {
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const { user } = useUserData();
	const { logout } = useUserData();

	const { status, isLoading: isStatusLoading } = useCarbonStatus('month');


	const isCurrent = (path: string) => window.location.pathname === path;
	const displayName = `${user?.display_name}${user?.business_data?.organization_name ? " (" + user.business_data.organization_name + ")" : ""}` || "";

	let statusMenu;
	if (isStatusLoading) {
		statusMenu = null;
	} else {
		switch (status) {
			case CarbonStatus.CarbonNeutral:
				statusMenu = (
					<div className="px-2">
						<p className="text-xs font-dm-sans font-bold uppercase text-gray-600 mb-1">
							{user?.business_data?.organization_name || user?.display_name}'s carbon status
						</p>
						<NeutralGreenBadge className="w-full" />
					</div>
				)
				break;
			case CarbonStatus.CarbonNegative:
				statusMenu = (
					<div className="px-2">
						<p className="text-xs font-dm-sans font-bold uppercase text-gray-600 mb-1">
							{user?.business_data?.organization_name || user?.display_name}'s carbon status
						</p>
						<NegativeGreenBadge className="w-full" />
					</div>
				)
				break;
			case CarbonStatus.NoCalculations:
				statusMenu = null;
				break;
			default:
				statusMenu = (
					<NavLink
						to={"/?go-neutral=true"}
						className={classNames(
							'text-gray-600 hover:bg-gray-50 hover:text-canopi-green',
							'group flex items-center px-2 py-2 text-sm font-medium rounded-md justify-between'
						)}
					>
						<div className="flex flex-row items-center">
							<GlobeIcon
								className={classNames(
									'text-gray-600 group-hover:text-canopi-green',
									'mr-3 flex-shrink-0 h-6 w-6'
								)}
								aria-hidden="true"
							/>
							Go carbon neutral

						</div>
						<ArrowSmRightIcon
							className={classNames(
								'text-gray-400 group-hover:text-canopi-green',
								'mr-3 flex-shrink-0 h-6 w-6'
							)}
							aria-hidden="true"
						/>
					</NavLink>
				)
				break;
		}
	}


	return (
		<div className="h-screen flex overflow-hidden bg-canopi-light-blue font-dm-sans">
			<Transition.Root show={sidebarOpen} as={Fragment}>
				<Dialog
					as="div"
					static
					className="fixed inset-0 flex z-40 md:hidden"
					open={sidebarOpen}
					onClose={setSidebarOpen}
				>
					<Transition.Child
						as={Fragment}
						enter="transition-opacity ease-linear duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition-opacity ease-linear duration-300"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
					</Transition.Child>
					<Transition.Child
						as={Fragment}
						enter="transition ease-in-out duration-300 transform"
						enterFrom="-translate-x-full"
						enterTo="translate-x-0"
						leave="transition ease-in-out duration-300 transform"
						leaveFrom="translate-x-0"
						leaveTo="-translate-x-full"
					>
						<div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
							<Transition.Child
								as={Fragment}
								enter="ease-in-out duration-300"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in-out duration-300"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<div className="absolute top-0 right-0 -mr-12 pt-2">
									<button
										className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
										onClick={() => setSidebarOpen(false)}
									>
										<span className="sr-only">Close sidebar</span>
										<XIcon className="h-6 w-6 text-white" aria-hidden="true" />
									</button>
								</div>
							</Transition.Child>
							<div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
								<div className="flex-shrink-0 flex items-center px-4">
									<svg width="167" height="45" viewBox="0 0 167 45" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M59.8646 34.4181C57.4026 34.4181 55.2891 33.8955 53.5239 32.8503C51.7588 31.7819 50.4 30.3071 49.4478 28.4258C48.4955 26.5213 48.0194 24.3265 48.0194 21.8413C48.0194 19.3561 48.4955 17.1613 49.4478 15.2568C50.4 13.3523 51.7588 11.8658 53.5239 10.7974C55.2891 9.72903 57.4026 9.19484 59.8646 9.19484C62.791 9.19484 65.1833 9.92645 67.0413 11.3897C68.9226 12.8297 70.0955 14.8619 70.56 17.4864H65.6478C65.3459 16.1626 64.6955 15.129 63.6968 14.3858C62.7213 13.6194 61.4207 13.2361 59.7949 13.2361C57.542 13.2361 55.7768 14.0026 54.4994 15.5355C53.222 17.0684 52.5833 19.1703 52.5833 21.8413C52.5833 24.5123 53.222 26.6142 54.4994 28.1471C55.7768 29.6568 57.542 30.4116 59.7949 30.4116C61.4207 30.4116 62.7213 30.0632 63.6968 29.3665C64.6955 28.6465 65.3459 27.6594 65.6478 26.4052H70.56C70.0955 28.9135 68.9226 30.8761 67.0413 32.2929C65.1833 33.7097 62.791 34.4181 59.8646 34.4181ZM80.3603 34.4181C78.8739 34.4181 77.6545 34.1858 76.7023 33.7213C75.75 33.2335 75.0416 32.5948 74.5771 31.8052C74.1126 31.0155 73.8803 30.1445 73.8803 29.1923C73.8803 27.5897 74.5074 26.289 75.7616 25.2903C77.0158 24.2916 78.8971 23.7923 81.4055 23.7923H85.7952V23.3742C85.7952 22.1897 85.4584 21.3187 84.7849 20.7613C84.1113 20.2039 83.2752 19.9252 82.2765 19.9252C81.3707 19.9252 80.581 20.1458 79.9074 20.5871C79.2339 21.0052 78.8158 21.6323 78.6532 22.4684H74.2984C74.4145 21.2142 74.8326 20.1226 75.5526 19.1935C76.2958 18.2645 77.2481 17.5561 78.4094 17.0684C79.5707 16.5574 80.8713 16.3019 82.3113 16.3019C84.7732 16.3019 86.7126 16.9174 88.1294 18.1484C89.5461 19.3794 90.2545 21.1213 90.2545 23.3742V34H86.4571L86.0391 31.2129C85.5281 32.1419 84.8081 32.9084 83.8791 33.5123C82.9732 34.1161 81.8003 34.4181 80.3603 34.4181ZM81.3707 30.9342C82.6481 30.9342 83.6352 30.5161 84.332 29.68C85.052 28.8439 85.5049 27.8103 85.6907 26.5794H81.8932C80.7087 26.5794 79.861 26.8 79.35 27.2413C78.8391 27.6594 78.5836 28.1819 78.5836 28.809C78.5836 29.4826 78.8391 30.0052 79.35 30.3768C79.861 30.7484 80.5345 30.9342 81.3707 30.9342ZM94.51 34V16.72H98.4468L98.7952 19.6465C99.3294 18.6245 100.096 17.8116 101.095 17.2077C102.116 16.6039 103.313 16.3019 104.683 16.3019C106.82 16.3019 108.48 16.9755 109.665 18.3226C110.849 19.6697 111.442 21.6439 111.442 24.2452V34H106.982V24.6632C106.982 23.1768 106.68 22.0387 106.076 21.249C105.473 20.4594 104.532 20.0645 103.255 20.0645C102 20.0645 100.967 20.5058 100.154 21.3884C99.3642 22.271 98.9694 23.5019 98.9694 25.0813V34H94.51ZM123.997 34.4181C122.325 34.4181 120.816 34.0348 119.468 33.2684C118.145 32.5019 117.088 31.4452 116.298 30.0981C115.532 28.7277 115.148 27.1484 115.148 25.36C115.148 23.5716 115.543 22.0039 116.333 20.6568C117.123 19.2865 118.179 18.2181 119.503 17.4516C120.85 16.6852 122.36 16.3019 124.032 16.3019C125.681 16.3019 127.168 16.6852 128.492 17.4516C129.839 18.2181 130.896 19.2865 131.662 20.6568C132.452 22.0039 132.846 23.5716 132.846 25.36C132.846 27.1484 132.452 28.7277 131.662 30.0981C130.896 31.4452 129.839 32.5019 128.492 33.2684C127.145 34.0348 125.646 34.4181 123.997 34.4181ZM123.997 30.551C125.159 30.551 126.169 30.1213 127.028 29.2619C127.888 28.3794 128.317 27.0787 128.317 25.36C128.317 23.6413 127.888 22.3523 127.028 21.4929C126.169 20.6103 125.17 20.169 124.032 20.169C122.848 20.169 121.826 20.6103 120.966 21.4929C120.13 22.3523 119.712 23.6413 119.712 25.36C119.712 27.0787 120.13 28.3794 120.966 29.2619C121.826 30.1213 122.836 30.551 123.997 30.551ZM136.834 41.6645V16.72H140.805L141.293 19.1935C141.85 18.4271 142.582 17.7535 143.488 17.1729C144.417 16.5923 145.613 16.3019 147.076 16.3019C148.702 16.3019 150.154 16.6968 151.431 17.4864C152.708 18.2761 153.719 19.3561 154.462 20.7265C155.205 22.0968 155.577 23.6529 155.577 25.3948C155.577 27.1368 155.205 28.6929 154.462 30.0632C153.719 31.4103 152.708 32.4787 151.431 33.2684C150.154 34.0348 148.702 34.4181 147.076 34.4181C145.776 34.4181 144.637 34.1742 143.662 33.6865C142.687 33.1987 141.897 32.5135 141.293 31.631V41.6645H136.834ZM146.136 30.5161C147.552 30.5161 148.725 30.04 149.654 29.0877C150.583 28.1355 151.048 26.9045 151.048 25.3948C151.048 23.8852 150.583 22.6426 149.654 21.6671C148.725 20.6916 147.552 20.2039 146.136 20.2039C144.696 20.2039 143.511 20.6916 142.582 21.6671C141.676 22.6194 141.223 23.8503 141.223 25.36C141.223 26.8697 141.676 28.1123 142.582 29.0877C143.511 30.04 144.696 30.5161 146.136 30.5161ZM161.964 14.0374C161.151 14.0374 160.478 13.7935 159.944 13.3058C159.433 12.8181 159.177 12.2026 159.177 11.4594C159.177 10.7161 159.433 10.1123 159.944 9.64774C160.478 9.16 161.151 8.91613 161.964 8.91613C162.777 8.91613 163.439 9.16 163.95 9.64774C164.484 10.1123 164.751 10.7161 164.751 11.4594C164.751 12.2026 164.484 12.8181 163.95 13.3058C163.439 13.7935 162.777 14.0374 161.964 14.0374ZM159.735 34V16.72H164.194V34H159.735Z" fill="#083232" />
										<path d="M34.8388 5.08057V8.7478C34.8388 26.4 24.9868 34.4184 12.8354 34.4184H5.50097C5.19064 36.2353 5.4871 38.0761 5.50097 39.9193H0.00012207C0.00012207 37.4201 1.41201 34.6605 1.83374 32.5848C1.41201 30.2121 0.91693 28.4005 0.91693 24.3335C0.91693 14.2065 10.0428 5.08057 20.1699 5.08057C23.8371 5.08057 27.5044 6.91418 34.8388 5.08057ZM20.1699 8.7478C16.2795 8.7478 12.5484 10.2933 9.7974 13.0442C7.04644 15.7952 5.50097 19.5263 5.50097 23.4167C5.50097 24.0805 5.50647 24.7204 5.51931 25.3347C7.81866 21.7078 11.187 18.8419 15.5932 16.3243L17.4121 19.5075C12.1772 22.4999 8.7043 25.8994 6.92386 30.7512H12.8354C23.8646 30.7512 30.9351 23.4662 31.1661 9.45924C28.6504 9.70311 26.3125 9.54726 23.4282 9.11452C21.3196 8.79731 20.9052 8.7478 20.1699 8.7478Z" fill="#2E856E" />
									</svg>
								</div>
								<nav className="mt-5 px-2 space-y-1">
									{navigation.map((item) => (
										<a
											key={item.name}
											href={item.href}
											className={classNames(
												isCurrent(item.href)
													? 'bg-gray-100 text-gray-900'
													: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
												'group flex items-center px-2 py-2 text-base font-medium rounded-md'
											)}
										>
											<item.icon
												className={classNames(
													isCurrent(item.href) ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
													'mr-4 flex-shrink-0 h-6 w-6'
												)}
												aria-hidden="true"
											/>
											{item.name}
										</a>
									))}
								</nav>
							</div>
							<div className="flex-shrink-0 flex border-t border-gray-200 p-4">
								<div className="flex-shrink-0 group block">
									<div className="flex items-center">
										<div>
											<img
												className="inline-block h-10 w-10 rounded-full"
												src={user?.picture || ""}
												alt=""
											/>
										</div>
										<div className="ml-3">
											<p className="text-base font-medium text-gray-700">{displayName}</p>
											<p className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer" onClick={() => logout()}>Log out</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Transition.Child>
					<div className="flex-shrink-0 w-14">{/* Force sidebar to shrink to fit close icon */}</div>
				</Dialog>
			</Transition.Root>

			{/* Static sidebar for desktop */}
			<div className="hidden md:flex md:flex-shrink-0">
				<div className="flex flex-col w-64">
					{/* Sidebar component, swap this element with another sidebar if you like */}
					<div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-canopi-light-blue">
						<div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
							<div className="flex items-center flex-shrink-0 px-4">
								<svg width="167" height="45" viewBox="0 0 167 45" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M59.8646 34.4181C57.4026 34.4181 55.2891 33.8955 53.5239 32.8503C51.7588 31.7819 50.4 30.3071 49.4478 28.4258C48.4955 26.5213 48.0194 24.3265 48.0194 21.8413C48.0194 19.3561 48.4955 17.1613 49.4478 15.2568C50.4 13.3523 51.7588 11.8658 53.5239 10.7974C55.2891 9.72903 57.4026 9.19484 59.8646 9.19484C62.791 9.19484 65.1833 9.92645 67.0413 11.3897C68.9226 12.8297 70.0955 14.8619 70.56 17.4864H65.6478C65.3459 16.1626 64.6955 15.129 63.6968 14.3858C62.7213 13.6194 61.4207 13.2361 59.7949 13.2361C57.542 13.2361 55.7768 14.0026 54.4994 15.5355C53.222 17.0684 52.5833 19.1703 52.5833 21.8413C52.5833 24.5123 53.222 26.6142 54.4994 28.1471C55.7768 29.6568 57.542 30.4116 59.7949 30.4116C61.4207 30.4116 62.7213 30.0632 63.6968 29.3665C64.6955 28.6465 65.3459 27.6594 65.6478 26.4052H70.56C70.0955 28.9135 68.9226 30.8761 67.0413 32.2929C65.1833 33.7097 62.791 34.4181 59.8646 34.4181ZM80.3603 34.4181C78.8739 34.4181 77.6545 34.1858 76.7023 33.7213C75.75 33.2335 75.0416 32.5948 74.5771 31.8052C74.1126 31.0155 73.8803 30.1445 73.8803 29.1923C73.8803 27.5897 74.5074 26.289 75.7616 25.2903C77.0158 24.2916 78.8971 23.7923 81.4055 23.7923H85.7952V23.3742C85.7952 22.1897 85.4584 21.3187 84.7849 20.7613C84.1113 20.2039 83.2752 19.9252 82.2765 19.9252C81.3707 19.9252 80.581 20.1458 79.9074 20.5871C79.2339 21.0052 78.8158 21.6323 78.6532 22.4684H74.2984C74.4145 21.2142 74.8326 20.1226 75.5526 19.1935C76.2958 18.2645 77.2481 17.5561 78.4094 17.0684C79.5707 16.5574 80.8713 16.3019 82.3113 16.3019C84.7732 16.3019 86.7126 16.9174 88.1294 18.1484C89.5461 19.3794 90.2545 21.1213 90.2545 23.3742V34H86.4571L86.0391 31.2129C85.5281 32.1419 84.8081 32.9084 83.8791 33.5123C82.9732 34.1161 81.8003 34.4181 80.3603 34.4181ZM81.3707 30.9342C82.6481 30.9342 83.6352 30.5161 84.332 29.68C85.052 28.8439 85.5049 27.8103 85.6907 26.5794H81.8932C80.7087 26.5794 79.861 26.8 79.35 27.2413C78.8391 27.6594 78.5836 28.1819 78.5836 28.809C78.5836 29.4826 78.8391 30.0052 79.35 30.3768C79.861 30.7484 80.5345 30.9342 81.3707 30.9342ZM94.51 34V16.72H98.4468L98.7952 19.6465C99.3294 18.6245 100.096 17.8116 101.095 17.2077C102.116 16.6039 103.313 16.3019 104.683 16.3019C106.82 16.3019 108.48 16.9755 109.665 18.3226C110.849 19.6697 111.442 21.6439 111.442 24.2452V34H106.982V24.6632C106.982 23.1768 106.68 22.0387 106.076 21.249C105.473 20.4594 104.532 20.0645 103.255 20.0645C102 20.0645 100.967 20.5058 100.154 21.3884C99.3642 22.271 98.9694 23.5019 98.9694 25.0813V34H94.51ZM123.997 34.4181C122.325 34.4181 120.816 34.0348 119.468 33.2684C118.145 32.5019 117.088 31.4452 116.298 30.0981C115.532 28.7277 115.148 27.1484 115.148 25.36C115.148 23.5716 115.543 22.0039 116.333 20.6568C117.123 19.2865 118.179 18.2181 119.503 17.4516C120.85 16.6852 122.36 16.3019 124.032 16.3019C125.681 16.3019 127.168 16.6852 128.492 17.4516C129.839 18.2181 130.896 19.2865 131.662 20.6568C132.452 22.0039 132.846 23.5716 132.846 25.36C132.846 27.1484 132.452 28.7277 131.662 30.0981C130.896 31.4452 129.839 32.5019 128.492 33.2684C127.145 34.0348 125.646 34.4181 123.997 34.4181ZM123.997 30.551C125.159 30.551 126.169 30.1213 127.028 29.2619C127.888 28.3794 128.317 27.0787 128.317 25.36C128.317 23.6413 127.888 22.3523 127.028 21.4929C126.169 20.6103 125.17 20.169 124.032 20.169C122.848 20.169 121.826 20.6103 120.966 21.4929C120.13 22.3523 119.712 23.6413 119.712 25.36C119.712 27.0787 120.13 28.3794 120.966 29.2619C121.826 30.1213 122.836 30.551 123.997 30.551ZM136.834 41.6645V16.72H140.805L141.293 19.1935C141.85 18.4271 142.582 17.7535 143.488 17.1729C144.417 16.5923 145.613 16.3019 147.076 16.3019C148.702 16.3019 150.154 16.6968 151.431 17.4864C152.708 18.2761 153.719 19.3561 154.462 20.7265C155.205 22.0968 155.577 23.6529 155.577 25.3948C155.577 27.1368 155.205 28.6929 154.462 30.0632C153.719 31.4103 152.708 32.4787 151.431 33.2684C150.154 34.0348 148.702 34.4181 147.076 34.4181C145.776 34.4181 144.637 34.1742 143.662 33.6865C142.687 33.1987 141.897 32.5135 141.293 31.631V41.6645H136.834ZM146.136 30.5161C147.552 30.5161 148.725 30.04 149.654 29.0877C150.583 28.1355 151.048 26.9045 151.048 25.3948C151.048 23.8852 150.583 22.6426 149.654 21.6671C148.725 20.6916 147.552 20.2039 146.136 20.2039C144.696 20.2039 143.511 20.6916 142.582 21.6671C141.676 22.6194 141.223 23.8503 141.223 25.36C141.223 26.8697 141.676 28.1123 142.582 29.0877C143.511 30.04 144.696 30.5161 146.136 30.5161ZM161.964 14.0374C161.151 14.0374 160.478 13.7935 159.944 13.3058C159.433 12.8181 159.177 12.2026 159.177 11.4594C159.177 10.7161 159.433 10.1123 159.944 9.64774C160.478 9.16 161.151 8.91613 161.964 8.91613C162.777 8.91613 163.439 9.16 163.95 9.64774C164.484 10.1123 164.751 10.7161 164.751 11.4594C164.751 12.2026 164.484 12.8181 163.95 13.3058C163.439 13.7935 162.777 14.0374 161.964 14.0374ZM159.735 34V16.72H164.194V34H159.735Z" fill="#083232" />
									<path d="M34.8388 5.08057V8.7478C34.8388 26.4 24.9868 34.4184 12.8354 34.4184H5.50097C5.19064 36.2353 5.4871 38.0761 5.50097 39.9193H0.00012207C0.00012207 37.4201 1.41201 34.6605 1.83374 32.5848C1.41201 30.2121 0.91693 28.4005 0.91693 24.3335C0.91693 14.2065 10.0428 5.08057 20.1699 5.08057C23.8371 5.08057 27.5044 6.91418 34.8388 5.08057ZM20.1699 8.7478C16.2795 8.7478 12.5484 10.2933 9.7974 13.0442C7.04644 15.7952 5.50097 19.5263 5.50097 23.4167C5.50097 24.0805 5.50647 24.7204 5.51931 25.3347C7.81866 21.7078 11.187 18.8419 15.5932 16.3243L17.4121 19.5075C12.1772 22.4999 8.7043 25.8994 6.92386 30.7512H12.8354C23.8646 30.7512 30.9351 23.4662 31.1661 9.45924C28.6504 9.70311 26.3125 9.54726 23.4282 9.11452C21.3196 8.79731 20.9052 8.7478 20.1699 8.7478Z" fill="#2E856E" />
								</svg>
							</div>
							<nav className="mt-5 flex-1 px-2 bg-canopi-light-blue space-y-1">
								{navigation.map((item) => (
									<NavLink
										key={item.name}
										to={item.href}
										className={classNames(
											isCurrent(item.href) ? 'bg-white text-canopi-green' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
											'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
										)}
									>
										<item.icon
											className={classNames(
												isCurrent(item.href) ? 'text-canopi-green' : 'text-gray-400 group-hover:text-gray-500',
												'mr-3 flex-shrink-0 h-6 w-6'
											)}
											aria-hidden="true"
										/>
										{item.name}
									</NavLink>
								))}
								<a
									href="https://canopi.notion.site/Canopi-Knowledge-Library-501b5a8507a540528097dbf523f1f98a"
									target="_blank"
									className={classNames(
										'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
										'group flex items-center px-2 py-2 text-sm font-medium rounded-md justify-between'
									)}
								>
									<div className="flex flex-row items-center">
										<BookOpenIcon
											className={classNames(
												'text-gray-400 group-hover:text-gray-500',
												'mr-3 flex-shrink-0 h-6 w-6'
											)}
											aria-hidden="true"
										/>
										Knowledge Base
									</div>
									<ExternalLinkIcon
										className={classNames(
											'text-gray-400 group-hover:text-gray-500',
											'mr-3 flex-shrink-0 h-6 w-6'
										)}
										aria-hidden="true"
									/>
								</a>
							</nav>
						</div>
						<div className="flex-shrink-0 flex border-t border-gray-200 py-4 px-2">
							<div className="flex-shrink-0 w-full group block">
								<div className="flex flex-col">
									{statusMenu}
								</div>
							</div>
						</div>
						<div className="flex-shrink-0 flex border-t border-gray-200 p-4">
							<div className="flex-shrink-0 w-full group block">
								<div className="flex items-center">
									<div>
										<img
											className="inline-block h-9 w-9 rounded-full"
											src={user?.picture || ""}
											alt=""
										/>
									</div>
									<div className="ml-3">
										<p className="text-sm font-medium text-gray-700">{displayName}</p>
										<p className="text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer" onClick={() => logout()}>Log out</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex flex-col w-0 flex-1 overflow-hidden">
				<div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
					<button
						className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
						onClick={() => setSidebarOpen(true)}
					>
						<span className="sr-only">Open sidebar</span>
						<MenuIcon className="h-6 w-6" aria-hidden="true" />
					</button>
				</div>
				<main className="flex-1 relative z-0 overflow-y-auto focus:outline-none px-10 pt-6 pb-6">
					{children}
				</main>
			</div>
		</div>
	)
}
